import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useStore } from '../store/useStore';
import { useIsMobile } from '../hooks/useIsMobile';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { editorUrlFor, DriveSharePanel } from './DriveEditor';
import { irA } from '../lib/dominios';
import CreateWithAIDialog from '../components/office/CreateWithAIDialog';
import GaleriaPlantillas from '../components/forms/editor/GaleriaPlantillas';
import DialogoCrearBuzon from '../components/forms/buzon/DialogoCrearBuzon';
import { BUZON_CASOS } from '../lib/modulos';
import { DialogoTexto, DialogoConfirmar } from '../components/drive/DialogosDrive';
import MenuContextual from '../components/drive/MenuContextual';
import { DRIVE_NAME, FILE_COLORS, OFFICE_APPS, OFFICE_NAME, AuroraLogo } from '../components/office/icons';
import '../components/drive/drive.css';
import { fechaHora12 } from '../lib/format';
import { encolar, ejecutar, seMostro } from '../lib/transferencias';
import {
  HardDrive, Upload, FileText, Table2, Presentation, Image as ImageIcon, File,
  Trash2, Download, Search, Loader2, Folder, Star, Clock, Users, List, LayoutGrid,
  Plus, ChevronRight, RotateCcw, FileType2, Workflow, MoreVertical, Pencil, Share2,
  ExternalLink, FolderPlus, FileCode2, FileArchive, Clapperboard, Music2, ArrowUpDown, X, ClipboardList, LayoutTemplate, MessageSquareWarning,
} from 'lucide-react';

/** Tipos de archivo: etiqueta, color (paleta Orvian, ver FILE_COLORS) e ícono */
const KIND = {
  doc: { label: 'Documento', color: FILE_COLORS.doc, Icon: FileText, ext: '.docx' },
  sheet: { label: 'Hoja de cálculo', color: FILE_COLORS.sheet, Icon: Table2, ext: '.xlsx' },
  slides: { label: 'Presentación', color: FILE_COLORS.slides, Icon: Presentation, ext: '.pptx' },
  pdf: { label: 'PDF', color: FILE_COLORS.pdf, Icon: FileType2, ext: '.pdf' },
  diagram: { label: 'Diagrama', color: FILE_COLORS.diagram, Icon: Workflow, ext: '.vsdx' },
  text: { label: 'Texto', color: FILE_COLORS.neutral, Icon: FileCode2, ext: '.txt' },
  form: { label: 'Formulario', color: FILE_COLORS.form, Icon: ClipboardList, ext: '' },
  image: { label: 'Imagen', color: FILE_COLORS.image, Icon: ImageIcon, ext: '' },
  folder: { label: 'Carpeta', color: FILE_COLORS.neutral, Icon: Folder, ext: '' },
  file: { label: 'Archivo', color: FILE_COLORS.neutral, Icon: File, ext: '' },
  archive: { label: 'Archivo comprimido', color: FILE_COLORS.neutral, Icon: FileArchive, ext: '' },
  video: { label: 'Vídeo', color: FILE_COLORS.pdf, Icon: Clapperboard, ext: '' },
  audio: { label: 'Audio', color: FILE_COLORS.slides, Icon: Music2, ext: '' },
};

const NAV = [
  { id: 'my', label: 'Mi unidad', icon: HardDrive },
  { id: 'shared', label: 'Compartidos conmigo', icon: Users },
  { id: 'recent', label: 'Recientes', icon: Clock },
  { id: 'starred', label: 'Destacados', icon: Star },
  { id: 'trash', label: 'Papelera', icon: Trash2 },
];

/** Tipo propio del portapapeles de arrastre: distingue una fila de Drive de un
 *  archivo del escritorio. En `dataTransfer.types` los tipos llegan siempre en
 *  minúsculas, por eso la constante ya lo está. */
const TIPO_ARRASTRE = 'application/x-orvian-drive';

function fmtSize(n) {
  if (!n) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

/* Filtros de la barra: tipo de archivo, cuándo se tocó y cuánto pesa. Son los
   tres con los que se busca de verdad cuando la unidad tiene cientos de cosas. */
const FILTRO_TIPO = [
  { id: 'all', label: 'Todos' },
  { id: 'doc', label: 'Documentos', kinds: ['doc'] },
  { id: 'sheet', label: 'Hojas de cálculo', kinds: ['sheet'] },
  { id: 'slides', label: 'Presentaciones', kinds: ['slides'] },
  { id: 'pdf', label: 'PDF', kinds: ['pdf'] },
  { id: 'image', label: 'Imágenes', kinds: ['image'] },
  { id: 'media', label: 'Vídeo y audio', kinds: ['video', 'audio'] },
  { id: 'text', label: 'Texto y código', kinds: ['text'] },
  { id: 'archive', label: 'Comprimidos', kinds: ['archive'] },
  { id: 'file', label: 'Otros', kinds: ['file', 'diagram'] },
];

const FILTRO_FECHA = [
  { id: 'all', label: 'Cualquier fecha' },
  { id: 'today', label: 'Últimas 24 horas', dias: 1 },
  { id: '7', label: 'Últimos 7 días', dias: 7 },
  { id: '30', label: 'Últimos 30 días', dias: 30 },
  { id: '365', label: 'Último año', dias: 365 },
];

const FILTRO_PESO = [
  { id: 'all', label: 'Cualquier tamaño' },
  { id: 'xs', label: 'Menos de 1 MB', max: 1024 * 1024 },
  { id: 's', label: '1 – 10 MB', min: 1024 * 1024, max: 10 * 1024 * 1024 },
  { id: 'm', label: '10 – 100 MB', min: 10 * 1024 * 1024, max: 100 * 1024 * 1024 },
  { id: 'l', label: 'Más de 100 MB', min: 100 * 1024 * 1024 },
];

/** ¿La fecha entra en la ventana del filtro? */
function dentroDeFecha(iso, filtro) {
  const f = FILTRO_FECHA.find((x) => x.id === filtro);
  if (!f?.dias || !iso) return !f?.dias;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return false;
  return Date.now() - t <= f.dias * 24 * 3600 * 1000;
}

/** ¿El peso entra en el tramo del filtro? */
function dentroDePeso(bytes, filtro) {
  const f = FILTRO_PESO.find((x) => x.id === filtro);
  if (!f || f.id === 'all') return true;
  const n = bytes || 0;
  return n >= (f.min || 0) && n < (f.max || Infinity);
}

function fmtDate(iso) {
  if (!iso) return '';
  try {
    return fechaHora12(iso);
  } catch {
    return '';
  }
}

/** Nombre legible de una ruta de carpeta ("/clientes/2026" → "2026"). */
function nombreCarpeta(ruta) {
  if (!ruta || ruta === '/') return 'Mi unidad';
  return ruta.split('/').filter(Boolean).pop() || 'Mi unidad';
}

/** ¿El arrastre trae archivos del sistema operativo? */
function traeArchivosDelSistema(e) {
  return Array.from(e.dataTransfer?.types || []).includes('Files');
}

/** ¿El arrastre trae una fila de Drive? */
function traeArchivoDeDrive(e) {
  return Array.from(e.dataTransfer?.types || []).includes(TIPO_ARRASTRE);
}

/**
 * Drive: explorador de archivos (interfaz tipo Google Drive).
 * Opening a file navigates to standalone editor: /document|spreadsheet|presentation|file/:id
 */
export default function Drive() {
  const qc = useQueryClient();
  const nav = useNavigate();
  const fileRef = useRef(null);
  // La búsqueda la escribe la barra superior (AppShell); en móvil, la caja de aquí.
  const q = useStore((s) => s.driveSearch);
  const setQ = useStore((s) => s.setDriveSearch);
  const user = useStore((s) => s.user);
  const isMobile = useIsMobile();
  useEffect(() => () => setQ(''), [setQ]);
  const [view, setView] = useState('my');
  const [folder, setFolder] = useState('/');
  const [layout, setLayout] = useState('list');
  // Filtros y orden de la lista (viven en la vista; no se guardan en el servidor)
  const [fTipo, setFTipo] = useState('all');
  const [fFecha, setFFecha] = useState('all');
  const [fPeso, setFPeso] = useState('all');
  const [orden, setOrden] = useState({ campo: 'updated_at', asc: false });
  /* Menú «Nuevo»: guarda DÓNDE abrirlo ({x, y}) o null. Antes era un booleano y el menú se
     pintaba dentro del <aside class="hidden sm:flex">, así que en el teléfono el botón «+»
     cambiaba el estado y no aparecía nada: no había forma de crear ni de subir nada. Ahora lo
     pinta MenuContextual en un portal a <body>, igual que el menú del clic derecho, y además
     cierra con Escape y con un clic fuera. */
  const [newMenu, setNewMenu] = useState(null);
  // Creando un archivo: evita que un segundo clic cree un duplicado antes de navegar al editor.
  const [creando, setCreando] = useState(false);
  const [aiCreateOpen, setAiCreateOpen] = useState(false);
  const [plantillasOpen, setPlantillasOpen] = useState(false);
  const [buzonOpen, setBuzonOpen] = useState(false);
  /* Ventanas propias en vez de los cuadros del navegador: `dialogo` guarda qué
     se está pidiendo (crear carpeta, renombrar) y a quién afecta. */
  const [dialogo, setDialogo] = useState(null);
  const [aEliminar, setAEliminar] = useState(null);
  // Menú contextual abierto: { x, y, tipo: 'archivo' | 'carpeta' | 'fondo', file, ruta }
  const [menu, setMenu] = useState(null);
  const cerrarMenu = useCallback(() => setMenu(null), []);
  // Qué se está compartiendo: { tipo: 'archivo' | 'carpeta', id, name }
  const [compartir, setCompartir] = useState(null);
  // Dentro de una carpeta que me compartieron: { id, name, ruta } (ruta RELATIVA a esa carpeta)
  const [compartida, setCompartida] = useState(null);
  // Arrastre: id de la fila que viaja, carpeta resaltada como destino y si hay
  // archivos del sistema sobrevolando el área.
  const [arrastrando, setArrastrando] = useState(null);
  const [destino, setDestino] = useState(null);
  const [soltarExterno, setSoltarExterno] = useState(false);
  // dragenter/dragleave burbujean desde CADA hijo: sin contador la capa
  // parpadearía al pasar de una fila a otra. Es el fallo clásico.
  const profundidad = useRef(0);

  const { data: folderData } = useQuery({
    queryKey: ['drive-folders'],
    queryFn: async () => (await api.get('/drive/folders')).data,
  });
  const folders = folderData?.folders || ['/'];
  // Peso y última modificación de cada carpeta, calculados por el backend con
  // lo que hay dentro (incluidas sus subcarpetas).
  const folderStats = useMemo(() => folderData?.stats || {}, [folderData]);
  // Mis carpetas que están compartidas (para la marca de personas en la lista)
  const compartidas = useMemo(() => folderData?.compartidas || {}, [folderData]);

  const { data, isFetching } = useQuery({
    queryKey: ['drive-files', q, folder, view],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set('view', view);
      if (q) params.set('q', q);
      if (view === 'my' && folder) params.set('folder', folder);
      return (await api.get(`/drive/files?${params}`)).data;
    },
  });
  const items = useMemo(() => data?.items || [], [data]);
  const quota = data?.quota;
  // Carpetas que otras personas me compartieron (viven en «Compartidos conmigo»)
  const carpetasCompartidas = useMemo(() => data?.carpetas || [], [data]);

  const enCompartida = view === 'shared' && !!compartida;
  const { data: contenido } = useQuery({
    queryKey: ['drive-compartida', compartida?.id, compartida?.ruta],
    enabled: enCompartida,
    queryFn: async () => (
      await api.get(`/drive/carpetas/${compartida.id}/contenido`, { params: { ruta: compartida.ruta || '/' } })
    ).data,
  });
  const puedeSubirAqui = enCompartida && !!contenido?.can_edit;

  // Una notificación de «te compartí una carpeta» entra por /drive?compartida=<id>, y
  // «Ver carpeta» del correo (un adjunto guardado) por /drive?carpeta=<ruta de Mi unidad>
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get('compartida');
    const ruta = params.get('carpeta');
    if (id) {
      setView('shared');
      setCompartida({ id, name: '', ruta: '/' });
    } else if (ruta && ruta.startsWith('/')) {
      setView('my');
      setFolder(ruta);
    }
  }, []);

  // Salir de «Compartidos conmigo» cierra la carpeta abierta
  useEffect(() => { if (view !== 'shared') setCompartida(null); }, [view]);

  // Mover solo tiene sentido en Mi unidad: en Papelera la carpeta da igual, en
  // Compartidos el archivo no es nuestro y Recientes es una vista, no un sitio.
  const puedeMover = view === 'my';
  const archivoArrastrado = useMemo(
    () => items.find((f) => f.id === arrastrando) || null,
    [items, arrastrando],
  );

  const crumbs = useMemo(() => {
    if (view !== 'my') return [];
    if (!folder || folder === '/') return [{ path: '/', label: 'Mi unidad' }];
    const parts = folder.split('/').filter(Boolean);
    const out = [{ path: '/', label: 'Mi unidad' }];
    let acc = '';
    parts.forEach((p) => {
      acc += `/${p}`;
      out.push({ path: acc, label: p });
    });
    return out;
  }, [folder, view]);

  // Documentos, hojas y presentaciones se abren en docs.orvian.mx; lo demás, aquí (editorUrlFor)
  const openFile = (f, e) => {
    const destino = editorUrlFor(f);
    if (e && (e.metaKey || e.ctrlKey || e.button === 1)) {
      window.open(destino, '_blank', 'noopener');
      return;
    }
    irA(nav, destino);
  };

  // Orvian Forms: el formulario nace en Drive y se abre en su editor (docs.orvian.mx)
  const crearFormulario = async () => {
    setNewMenu(null);
    if (creando) return;
    setCreando(true);
    // Entre el clic y el editor pasan unos segundos sin nada en pantalla (C3): aviso de carga.
    const aviso = toast.loading('Creando formulario…');
    try {
      const { data } = await api.post('/forms', { folder });
      toast.success('Formulario creado', { id: aviso });
      await qc.invalidateQueries({ queryKey: ['drive-files'] });
      irA(nav, editorUrlFor(data.archivo));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo crear el formulario', { id: aviso });
    } finally {
      setCreando(false);
    }
  };

  /* Crear un archivo vacío. Entre el clic y la llegada al editor hay una petición: sin el
     cerrojo `creando`, dos clics seguidos creaban dos archivos. */
  const createNative = async (kind) => {
    setNewMenu(null);
    if (creando) return;
    setCreando(true);
    const fd = new FormData();
    fd.append('kind', kind);
    fd.append('native', '1');
    fd.append('folder', folder);
    const aviso = toast.loading(`Creando ${(KIND[kind]?.label || 'archivo').toLowerCase()}…`);   // C3
    try {
      const res = await api.post('/drive/files', fd);
      toast.success(`${KIND[kind]?.label || 'Archivo'} creado`, { id: aviso });
      await qc.invalidateQueries({ queryKey: ['drive-files'] });
      if (res.data) irA(nav, editorUrlFor(res.data));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo crear', { id: aviso });
    } finally {
      setCreando(false);
    }
  };

  /** Sube archivos a `destinoRuta` (por defecto, la carpeta abierta).
   *  El tope lo impone el backend: aquí solo se enseña su mensaje. Dentro de una carpeta que me
   *  compartieron como editor, la ruta es relativa a ella y el archivo queda en la carpeta (su
   *  dueño es quien la comparte; el backend anota quién lo subió). */
  const upload = async (fileList, destinoRuta = enCompartida ? (compartida.ruta || '/') : folder) => {
    if (enCompartida && !puedeSubirAqui) {
      toast.error('Necesitas permiso de editor en esta carpeta para añadir archivos');
      return;
    }
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const donde = enCompartida
      ? (compartida.name || 'la carpeta')
      : (destinoRuta === folder ? DRIVE_NAME : nombreCarpeta(destinoRuta));
    const compartidaId = enCompartida ? compartida.id : null;
    /* Cada archivo pasa por el gestor de subidas (lib/transferencias.js): lo pesado se ve en la barra de abajo
       con su progreso, velocidad y tiempo restante, se puede cancelar y sigue aunque se salga de Drive. */
    const refrescar = () => {
      qc.invalidateQueries({ queryKey: ['drive-files'] });
      qc.invalidateQueries({ queryKey: ['drive-folders'] });
      qc.invalidateQueries({ queryKey: ['drive-compartida'] });
    };
    const ids = encolar(files, { destino: donde });
    /* Los fallos se juntan y se cuentan UNA vez. Un aviso por archivo convertía una tanda de
       veinte en doce avisos apilados, sin decir cuántos entraron ni cuáles quedaron fuera. */
    const fallidos = [];
    let cancelados = 0;
    for (let i = 0; i < files.length; i += 1) {
      const file = files[i];
      try {
        await ejecutar(ids[i], (cfg) => {
          const fd = new FormData();
          fd.append('file', file);
          fd.append('folder', destinoRuta);
          if (compartidaId) fd.append('compartida', compartidaId);
          return api.post('/drive/files', fd, cfg);
        }, { alTerminar: refrescar });
      } catch (err) {
        if (err?.cancelado) cancelados += 1;
        else fallidos.push({ nombre: file.name, motivo: err.response?.data?.detail || 'no se pudo subir' });
      }
    }
    const bien = files.length - fallidos.length - cancelados;
    // Si la tanda se vio en la barra de abajo, ella ya cuenta cómo acabó: sin avisos repetidos
    if (seMostro(ids)) return;
    if (bien) {
      toast.success(files.length === 1 ? `Subido a ${donde}` : `${bien} de ${files.length} subidos a ${donde}`);
    }
    if (fallidos.length) {
      const lista = fallidos.slice(0, 3).map((f) => `${f.nombre} (${f.motivo})`).join('; ');
      const resto = fallidos.length > 3 ? ` y ${fallidos.length - 3} más` : '';
      toast.error(
        fallidos.length === 1 ? `No se subió ${lista}` : `${fallidos.length} archivos sin subir`,
        { description: `${lista}${resto}`, duration: 8000 },
      );
    }
  };

  const crearCarpeta = (padre = folder) => setDialogo({ tipo: 'carpeta', padre });

  const crearCarpetaCon = async (nombre, padre) => {
    // «/» en el nombre creaba una jerarquía fantasma (C2): mismo criterio que al renombrar.
    const limpio = String(nombre || '').replace(/\//g, ' ').replace(/\s+/g, ' ').trim();
    if (!limpio) { toast.error('Escribe un nombre para la carpeta'); return; }
    const base = padre === '/' ? '' : padre;
    const full = `${base}/${limpio}`.replace(/\/+/g, '/');
    try {
      await api.post('/drive/folders', { path: full });
      toast.success(limpio === String(nombre).trim() ? 'Carpeta creada' : `Carpeta creada como «${limpio}» (el nombre no puede llevar «/»)`);
      qc.invalidateQueries({ queryKey: ['drive-folders'] });
      setFolder(full);
      setView('my');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo crear la carpeta');
    }
  };

  /** Compartir una carpeta: la registra como compartible (idempotente) y abre el MISMO panel
   *  de permisos que un archivo. */
  const compartirCarpeta = async (ruta) => {
    try {
      const { data: c } = await api.post('/drive/carpetas', { path: ruta });
      setCompartir({ tipo: 'carpeta', id: c.id, name: c.name });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo compartir la carpeta');
    }
  };

  const toggleStar = async (f, e) => {
    e?.stopPropagation();
    try {
      await api.post(`/drive/files/${f.id}/star?starred=${!f.starred}`);
      qc.invalidateQueries({ queryKey: ['drive-files'] });
    } catch {
      toast.error('No se pudo destacar');
    }
  };

  const trashFile = async (f, e) => {
    e?.stopPropagation();
    // Dentro de la Papelera, DELETE borra de verdad y no hay vuelta atrás:
    // se pregunta antes, con el nombre delante.
    if (f.trashed || view === 'trash') {
      setAEliminar(f);
      return;
    }
    try {
      await api.delete(`/drive/files/${f.id}`);
      toast.success('Movido a la papelera');
      qc.invalidateQueries({ queryKey: ['drive-files'] });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo eliminar');
    }
  };

  /** Renombrar arrastra la ruta de los archivos y de las subcarpetas (lo hace el backend). */
  const renombrarCarpeta = async (ruta, nombre) => {
    try {
      const r = await api.put('/drive/folders', { path: ruta, nombre });
      toast.success(r.data?.movidos ? `Carpeta renombrada · ${r.data.movidos} archivo(s) actualizados` : 'Carpeta renombrada');
      qc.invalidateQueries({ queryKey: ['drive-folders'] });
      qc.invalidateQueries({ queryKey: ['drive-files'] });
      // Si estabas dentro de ella, te quedas dentro con el nombre nuevo
      if (folder === ruta || folder.startsWith(`${ruta}/`)) setFolder(r.data.path + folder.slice(ruta.length));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo cambiar el nombre');
    }
  };

  /* Una carpeta con archivos dentro NO se borra: el backend responde 409 y se
     dice cuántos hay. Borrar una carpeta no puede ser perder documentos. */
  const eliminarCarpeta = async (ruta) => {
    try {
      await api.delete('/drive/folders', { params: { path: ruta } });
      toast.success('Carpeta eliminada');
      qc.invalidateQueries({ queryKey: ['drive-folders'] });
      if (folder === ruta || folder.startsWith(`${ruta}/`)) setFolder(ruta.split('/').slice(0, -1).join('/') || '/');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo eliminar la carpeta');
    }
  };

  const eliminarParaSiempre = async (f) => {
    try {
      await api.delete(`/drive/files/${f.id}`);
      toast.success('Eliminado para siempre');
      qc.invalidateQueries({ queryKey: ['drive-files'] });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo eliminar');
    }
  };

  const restoreFile = async (f, e) => {
    e?.stopPropagation();
    try {
      await api.post(`/drive/files/${f.id}/restore`);
      toast.success('Restaurado');
      qc.invalidateQueries({ queryKey: ['drive-files'] });
    } catch {
      toast.error('No se pudo restaurar');
    }
  };

  const renombrar = (f) => setDialogo({ tipo: 'renombrar', archivo: f });

  const renombrarCon = async (f, limpio) => {
    if (limpio === f.name) return;
    try {
      await api.put(`/drive/files/${f.id}`, { name: limpio });
      toast.success('Nombre cambiado');
      qc.invalidateQueries({ queryKey: ['drive-files'] });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo cambiar el nombre');
    }
  };

  /** Mueve un archivo de carpeta. Devuelve true si el backend lo confirmó. */
  const mover = async (f, destinoRuta, { avisar = true } = {}) => {
    const origen = f.folder || '/';
    if (origen === destinoRuta) return false;
    try {
      await api.put(`/drive/files/${f.id}`, { folder: destinoRuta });
      qc.invalidateQueries({ queryKey: ['drive-files'] });
      qc.invalidateQueries({ queryKey: ['drive-folders'] });
      if (avisar) {
        toast.success(`«${f.name}» está en ${nombreCarpeta(destinoRuta)}`, {
          duration: 6000,
          // Deshacer = el mismo movimiento al revés; el backend no guarda
          // historial de carpetas, así que la ruta de origen la recordamos aquí.
          action: {
            label: 'Deshacer',
            onClick: async () => {
              const ok = await mover({ ...f, folder: destinoRuta }, origen, { avisar: false });
              if (ok) toast.success(`«${f.name}» vuelve a ${nombreCarpeta(origen)}`);
            },
          },
        });
      }
      return true;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo mover');
      return false;
    }
  };

  const download = async (f) => {
    try {
      const res = await api.get(`/drive/files/${f.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = f.name;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('No se pudo descargar');
    }
  };

  // En "Compartidos conmigo" y "Recientes" puede haber archivos de otra persona:
  // ahí solo el dueño renombra o comparte. Las demás vistas ya las filtra el
  // backend por owner_id, así que todo lo que llega es propio.
  const esPropietario = (f) => (view !== 'shared' && view !== 'recent') || f.owner_id === user?.id;

  // ---------- Arrastrar y soltar ----------

  /** Baja la capa de subida y el resaltado, sin tocar el arrastre interno. */
  const limpiarExterno = useCallback(() => {
    profundidad.current = 0;
    setSoltarExterno(false);
    setDestino(null);
  }, []);

  const limpiarArrastre = useCallback(() => {
    limpiarExterno();
    setArrastrando(null);
  }, [limpiarExterno]);

  useEffect(() => {
    // Sacar el puntero de la ventana con el arrastre en marcha no dispara un
    // dragleave sobre el contenedor: sin esta red la capa "Suelta para subir"
    // se queda pegada para siempre. `relatedTarget` vacío = se fue de la ventana.
    const alSalirVentana = (e) => { if (!e.relatedTarget) limpiarExterno(); };
    // Perder el foco (cambiar de ventana a medio arrastre) tampoco deja rastro.
    // Solo se limpia lo externo: algún navegador manda blur al empezar un
    // arrastre propio, y borrar ahí `arrastrando` mataría el movimiento.
    const alPerderFoco = () => limpiarExterno();
    // Y soltar un archivo FUERA del área haría que el navegador abandonara la
    // aplicación para mostrarlo: mientras Drive está en pantalla, no.
    const tragarFuera = (e) => { if (traeArchivosDelSistema(e)) e.preventDefault(); };
    window.addEventListener('dragleave', alSalirVentana);
    window.addEventListener('dragend', limpiarArrastre);
    window.addEventListener('drop', limpiarArrastre);
    window.addEventListener('blur', alPerderFoco);
    document.addEventListener('dragover', tragarFuera);
    document.addEventListener('drop', tragarFuera);
    return () => {
      window.removeEventListener('dragleave', alSalirVentana);
      window.removeEventListener('dragend', limpiarArrastre);
      window.removeEventListener('drop', limpiarArrastre);
      window.removeEventListener('blur', alPerderFoco);
      document.removeEventListener('dragover', tragarFuera);
      document.removeEventListener('drop', tragarFuera);
    };
  }, [limpiarArrastre, limpiarExterno]);

  const iniciarArrastre = (e, f) => {
    if (!puedeMover) { e.preventDefault(); return; }
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData(TIPO_ARRASTRE, f.id);
    e.dataTransfer.setData('text/plain', f.name);
    setArrastrando(f.id);
  };

  /** Carpeta (chip o miga) sobrevolada: vale para mover y para subir. */
  const sobrevolarCarpeta = (e, ruta) => {
    const deDrive = traeArchivoDeDrive(e);
    const delSistema = traeArchivosDelSistema(e);
    if (!deDrive && !delSistema) return;
    // Soltar donde ya está no mueve nada: ni se acepta ni se resalta.
    if (deDrive && (!puedeMover || (archivoArrastrado?.folder || '/') === ruta)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = deDrive ? 'move' : 'copy';
    setDestino(ruta);
  };

  const salirDeCarpeta = (e, ruta) => {
    // Pasar del botón a su propio icono también dispara dragleave: si no se
    // filtra, el resaltado parpadea.
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setDestino((d) => (d === ruta ? null : d));
  };

  const soltarEnCarpeta = async (e, ruta) => {
    const deDrive = traeArchivoDeDrive(e);
    const delSistema = traeArchivosDelSistema(e);
    if (!deDrive && !delSistema) return;
    e.preventDefault();
    e.stopPropagation(); // el área de abajo no debe subirlo otra vez
    const archivos = delSistema ? Array.from(e.dataTransfer.files || []) : [];
    const id = deDrive ? e.dataTransfer.getData(TIPO_ARRASTRE) : null;
    limpiarArrastre();
    if (deDrive) {
      const f = items.find((x) => x.id === id);
      if (f && puedeMover) await mover(f, ruta);
      return;
    }
    if (archivos.length) await upload(archivos, ruta);
  };

  /** Propiedades comunes de cualquier carpeta que acepte sueltas. */
  const propsCarpetaDestino = (ruta) => ({
    onDragOver: (e) => sobrevolarCarpeta(e, ruta),
    onDragLeave: (e) => salirDeCarpeta(e, ruta),
    onDrop: (e) => soltarEnCarpeta(e, ruta),
  });

  const entrarEnArea = (e) => {
    if (!traeArchivosDelSistema(e)) return;
    profundidad.current += 1;
    setSoltarExterno(true);
  };

  const salirDelArea = (e) => {
    if (!traeArchivosDelSistema(e)) return;
    profundidad.current -= 1;
    if (profundidad.current <= 0) {
      profundidad.current = 0;
      setSoltarExterno(false);
      setDestino(null);
    }
  };

  const sobrevolarArea = (e) => {
    // El arrastre interno solo se acepta encima de una carpeta, nunca sobre el
    // fondo: "soltar en ninguna parte" no significa nada.
    if (!traeArchivosDelSistema(e)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const soltarEnArea = async (e) => {
    if (!traeArchivosDelSistema(e)) return;
    e.preventDefault();
    const archivos = Array.from(e.dataTransfer.files || []);
    const ruta = destino || folder;
    limpiarArrastre();
    if (archivos.length) await upload(archivos, ruta);
  };

  // ---------- Menú contextual ----------

  const abrirMenu = (e, datos) => {
    e.preventDefault();
    e.stopPropagation(); // que el menú de la fila gane al del fondo
    setNewMenu(null);
    setMenu({ x: e.clientX, y: e.clientY, ...datos });
  };

  const accionesArchivo = (f) => {
    const mio = esPropietario(f);
    const enPapelera = view === 'trash' || f.trashed;
    const Icono = (KIND[f.kind] || KIND.file).Icon;
    return [
      { id: 'abrir', label: 'Abrir', Icon: Icono, onSelect: () => openFile(f) },
      {
        id: 'abrir-pestana',
        label: 'Abrir en una pestaña nueva',
        Icon: ExternalLink,
        onSelect: () => window.open(editorUrlFor(f), '_blank', 'noopener'),
      },
      { sep: true },
      mio && {
        id: 'destacar',
        label: f.starred ? 'Quitar de destacados' : 'Destacar',
        Icon: Star,
        onSelect: () => toggleStar(f),
      },
      mio && !enPapelera && {
        id: 'compartir',
        label: 'Compartir',
        Icon: Share2,
        onSelect: () => setCompartir({ tipo: 'archivo', id: f.id, name: f.name }),
      },
      mio && !enPapelera && {
        id: 'renombrar',
        label: 'Cambiar nombre',
        Icon: Pencil,
        onSelect: () => renombrar(f),
      },
      { id: 'descargar', label: 'Descargar', Icon: Download, onSelect: () => download(f) },
      { sep: true },
      enPapelera && {
        id: 'restaurar',
        label: 'Restaurar',
        Icon: RotateCcw,
        onSelect: () => restoreFile(f),
      },
      mio && {
        id: 'eliminar',
        label: enPapelera ? 'Eliminar para siempre' : 'Mover a la papelera',
        Icon: Trash2,
        danger: true,
        onSelect: () => trashFile(f),
      },
    ];
  };

  /** Abre el menú «Nuevo» justo debajo del botón que lo pidió, sea el de la barra lateral o el
   *  «+» del teléfono. Los dos abren EL MISMO menú: no hay una lista de creación por tamaño de
   *  pantalla que se pueda quedar atrás. */
  const abrirMenuNuevo = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    setNewMenu((abierto) => (abierto ? null : { x: r.left, y: r.bottom + 6 }));
  };

  const accionesNuevo = () => [
    { id: 'nuevo-ia', label: 'Crear con Aurora IA', Icon: AuroraLogo, destacado: true, onSelect: () => setAiCreateOpen(true) },
    { sep: true },
    { id: 'nuevo-doc', label: `Documento · ${OFFICE_APPS.doc.name} (.docx)`, Icon: FileText, color: FILE_COLORS.doc, disabled: creando, onSelect: () => createNative('doc') },
    { id: 'nuevo-sheet', label: `Hoja de cálculo · ${OFFICE_APPS.sheet.name} (.xlsx)`, Icon: Table2, color: FILE_COLORS.sheet, disabled: creando, onSelect: () => createNative('sheet') },
    { id: 'nuevo-slides', label: `Presentación · ${OFFICE_APPS.slides.name} (.pptx)`, Icon: Presentation, color: FILE_COLORS.slides, disabled: creando, onSelect: () => createNative('slides') },
    { id: 'nuevo-form', label: `Formulario · ${OFFICE_APPS.form.name}`, Icon: ClipboardList, color: FILE_COLORS.form, disabled: creando, onSelect: crearFormulario },
    { id: 'nuevo-form-plantilla', label: 'Formulario desde una plantilla', Icon: LayoutTemplate, color: FILE_COLORS.form, onSelect: () => setPlantillasOpen(true) },
    ...(BUZON_CASOS ? [{ id: 'nuevo-buzon', label: 'Buzón de quejas y sugerencias', Icon: MessageSquareWarning, color: FILE_COLORS.form, onSelect: () => setBuzonOpen(true) }] : []),
    { sep: true },
    { id: 'nueva-carpeta-menu', label: 'Nueva carpeta', Icon: Folder, onSelect: () => crearCarpeta(folder) },
    { id: 'subir-menu', label: 'Subir archivo', Icon: Upload, onSelect: () => fileRef.current?.click() },
  ];

  // El fondo solo ofrece crear: en Papelera, Compartidos o Recientes no hay
  // carpeta donde dejar lo nuevo.
  const accionesFondo = () => [
    { id: 'nueva-carpeta', label: 'Nueva carpeta', Icon: FolderPlus, onSelect: () => crearCarpeta(folder) },
    { id: 'subir', label: 'Subir archivo', Icon: Upload, onSelect: () => fileRef.current?.click() },
    { sep: true },
    { id: 'crear-ia', label: 'Crear con Aurora IA', Icon: AuroraLogo, onSelect: () => setAiCreateOpen(true) },
    { id: 'crear-formulario', label: 'Nuevo formulario', Icon: ClipboardList, onSelect: crearFormulario },
  ];

  // Las carpetas son rutas virtuales (no hay documento que renombrar ni borrar
  // en el backend), así que su menú solo ofrece lo que de verdad existe.
  const accionesCarpeta = (ruta) => [
    { id: 'abrir-carpeta', label: 'Abrir', Icon: Folder, onSelect: () => { setView('my'); setFolder(ruta); } },
    { sep: true },
    ruta !== '/' && {
      id: 'compartir-carpeta',
      label: compartidas[ruta] ? 'Compartir · gestionar acceso' : 'Compartir',
      Icon: Share2,
      onSelect: () => compartirCarpeta(ruta),
    },
    { id: 'subcarpeta', label: 'Nueva subcarpeta', Icon: FolderPlus, onSelect: () => crearCarpeta(ruta) },
    {
      id: 'subir-aqui',
      label: 'Subir archivos aquí',
      Icon: Upload,
      onSelect: () => { setFolder(ruta); setView('my'); fileRef.current?.click(); },
    },
    { sep: true },
    {
      id: 'renombrar-carpeta',
      label: 'Cambiar nombre',
      Icon: Pencil,
      onSelect: () => setDialogo({ tipo: 'renombrar-carpeta', ruta }),
    },
    {
      id: 'eliminar-carpeta',
      label: 'Eliminar carpeta',
      Icon: Trash2,
      danger: true,
      onSelect: () => eliminarCarpeta(ruta),
    },
  ];

  const opcionesMenu = () => {
    if (!menu) return [];
    if (menu.tipo === 'archivo') return accionesArchivo(menu.file);
    if (menu.tipo === 'carpeta') return accionesCarpeta(menu.ruta);
    return accionesFondo();
  };

  const subcarpetas = folders.filter((f) => {
    if (f === folder || f === '/') return false;
    const parent = folder === '/' ? '/' : folder;
    if (parent === '/') return f.startsWith('/') && f.slice(1).split('/').length === 1;
    if (!f.startsWith(`${parent}/`)) return false;
    const rest = f.slice(parent.length + 1);
    return rest && !rest.includes('/');
  });

  const hayFiltro = fTipo !== 'all' || fFecha !== 'all' || fPeso !== 'all';

  /** Ordena por la columna elegida; el nombre con orden natural del idioma. */
  const comparar = useCallback((a, b) => {
    const signo = orden.asc ? 1 : -1;
    if (orden.campo === 'name') return signo * (a.name || '').localeCompare(b.name || '', 'es', { numeric: true });
    if (orden.campo === 'size') return signo * ((a.size || 0) - (b.size || 0));
    return signo * (new Date(a.updated_at || 0) - new Date(b.updated_at || 0));
  }, [orden]);

  /* Las tres clases de fila de carpeta caben en la misma forma: mis subcarpetas, las que otras
     personas me compartieron («Compartidos conmigo») y las subcarpetas de una de esas. `ruta` solo
     la tienen las mías: es lo que las hace destino de un arrastre y les da menú. */
  const carpetasDelSitio = useMemo(() => {
    if (enCompartida) {
      return (contenido?.carpetas || []).map((c) => ({
        clave: `sub:${c.ruta}`,
        name: c.name,
        size: c.size,
        updated_at: c.updated_at,
        dueno: contenido?.carpeta?.owner_name || '—',
        abrir: () => setCompartida((v) => ({ ...v, ruta: c.ruta })),
      }));
    }
    if (view === 'shared') {
      return carpetasCompartidas.map((c) => ({
        clave: `compartida:${c.id}`,
        name: c.name,
        size: c.size,
        updated_at: c.updated_at,
        dueno: c.owner_name || '—',
        compartida: true,
        abrir: () => setCompartida({ id: c.id, name: c.name, ruta: '/' }),
      }));
    }
    if (view !== 'my') return [];
    return subcarpetas.map((ruta) => ({
      clave: ruta,
      ruta,
      name: nombreCarpeta(ruta),
      dueno: 'yo',
      compartida: !!compartidas[ruta],
      ...(folderStats[ruta] || { size: 0, updated_at: null }),
      abrir: () => { setView('my'); setFolder(ruta); },
    }));
  }, [enCompartida, contenido, view, carpetasCompartidas, subcarpetas, folderStats, compartidas]);

  // Las carpetas se ordenan y filtran con los mismos criterios que los archivos
  // (ya traen peso y fecha), pero un filtro por tipo de archivo las deja fuera:
  // una carpeta no es un PDF.
  const carpetasVisibles = useMemo(() => {
    if (fTipo !== 'all' || fPeso !== 'all') return [];
    return carpetasDelSitio.filter((c) => dentroDeFecha(c.updated_at, fFecha)).sort(comparar);
  }, [carpetasDelSitio, fTipo, fPeso, fFecha, comparar]);

  const visibles = useMemo(() => {
    const kinds = FILTRO_TIPO.find((t) => t.id === fTipo)?.kinds;
    return (enCompartida ? (contenido?.items || []) : items)
      .filter((f) => (!kinds || kinds.includes(f.kind || 'file'))
        && dentroDeFecha(f.updated_at, fFecha)
        && dentroDePeso(f.size, fPeso))
      .sort(comparar);
  }, [items, enCompartida, contenido, fTipo, fFecha, fPeso, comparar]);

  /** Encabezado de columna que ordena al pulsarlo. */
  const Columna = ({ campo, children, className = '' }) => (
    <th className={`text-left ${className}`}>
      <button
        type="button"
        className={`gdrive-col-sort ${orden.campo === campo ? 'activa' : ''}`}
        onClick={() => setOrden((o) => ({ campo, asc: o.campo === campo ? !o.asc : campo === 'name' }))}
        data-testid={`drive-orden-${campo}`}
      >
        {children}
        {orden.campo === campo && <ArrowUpDown className="h-3.5 w-3.5" style={{ transform: orden.asc ? 'none' : 'scaleY(-1)' }} />}
      </button>
    </th>
  );

  const barraFiltros = (
    <div className="gdrive-filtros" data-testid="drive-filtros">
      {[
        { valor: fTipo, set: setFTipo, ops: FILTRO_TIPO, etiqueta: 'Tipo', test: 'tipo' },
        { valor: fFecha, set: setFFecha, ops: FILTRO_FECHA, etiqueta: 'Modificado', test: 'fecha' },
        { valor: fPeso, set: setFPeso, ops: FILTRO_PESO, etiqueta: 'Tamaño', test: 'peso' },
      ].map((f) => (
        <label key={f.test} className={`gdrive-filtro ${f.valor !== 'all' ? 'activo' : ''}`}>
          <span className="sr-only">{f.etiqueta}</span>
          <select
            value={f.valor}
            onChange={(e) => f.set(e.target.value)}
            aria-label={f.etiqueta}
            data-testid={`drive-filtro-${f.test}`}
          >
            {f.ops.map((o) => (
              <option key={o.id} value={o.id}>{o.id === 'all' ? o.label : `${f.etiqueta}: ${o.label}`}</option>
            ))}
          </select>
        </label>
      ))}
      {hayFiltro && (
        <button
          type="button"
          className="gdrive-filtro-limpiar"
          onClick={() => { setFTipo('all'); setFFecha('all'); setFPeso('all'); }}
          data-testid="drive-filtros-limpiar"
        >
          <X className="h-3.5 w-3.5" /> Quitar filtros
        </button>
      )}
    </div>
  );

  const botonesVista = (
    <>
      <button type="button" className={`gdrive-icon-btn ${layout === 'list' ? 'active' : ''}`} onClick={() => setLayout('list')} aria-label="Lista"><List className="h-5 w-5" /></button>
      <button type="button" className={`gdrive-icon-btn ${layout === 'grid' ? 'active' : ''}`} onClick={() => setLayout('grid')} aria-label="Cuadrícula"><LayoutGrid className="h-5 w-5" /></button>
    </>
  );

  return (
    <div className="gdrive-root h-full flex" data-testid="drive-page">
      <aside className="gdrive-nav hidden sm:flex flex-col shrink-0">
        <div className="relative px-3 pt-3 pb-2">
          <Button
            className="gdrive-new-btn w-full justify-center gap-2"
            onClick={abrirMenuNuevo}
            data-testid="drive-new"
            aria-haspopup="menu"
            aria-expanded={!!newMenu}
            title="Nuevo"
          >
            <Plus className="h-5 w-5" /> <span className="gdrive-nav-text">Nuevo</span>
          </Button>
          <input
            ref={fileRef}
            type="file"
            multiple
            hidden
            onChange={(e) => { setNewMenu(null); upload(e.target.files); }}
          />
        </div>
        <nav className="flex-1 px-2 space-y-0.5">
          {NAV.map((n) => {
            const Icon = n.icon;
            const active = view === n.id;
            return (
              <button
                key={n.id}
                type="button"
                className={`gdrive-nav-item ${active ? 'active' : ''}`}
                onClick={() => { setView(n.id); if (n.id === 'my') setFolder('/'); }}
                title={n.label}
                aria-label={n.label}
              >
                <Icon className="h-[18px] w-[18px]" />
                <span className="gdrive-nav-text">{n.label}</span>
              </button>
            );
          })}
        </nav>
        {quota && (
          <div className="gdrive-quota">
            <div className="h-1 rounded-full mb-1.5 overflow-hidden" style={{ background: 'var(--gd-muted)' }}>
              <div className="h-full rounded-full" style={{ width: `${Math.min(100, quota.pct || 0)}%`, background: 'var(--gd-blue)' }} />
            </div>
            {quota.used_bytes ? fmtSize(quota.used_bytes) : '0 B'} de {fmtSize(quota.quota_bytes)} usados
          </div>
        )}
      </aside>

      <div
        className="flex-1 min-w-0 flex flex-col relative"
        style={{ background: 'var(--gd-bg)', color: 'var(--gd-text)' }}
        onDragEnter={entrarEnArea}
        onDragOver={sobrevolarArea}
        onDragLeave={salirDelArea}
        onDrop={soltarEnArea}
      >
        {/* En escritorio la búsqueda vive en la barra superior (AppShell) y los
            botones de vista suben a la fila de la ruta; en móvil, fila propia. */}
        {isMobile && (
          <div className="gdrive-topbar shrink-0">
            <div className="gdrive-search">
              <Search className="h-5 w-5 shrink-0" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Buscar en Drive"
                data-testid="drive-search"
              />
            </div>
            <div className="flex items-center gap-1 ml-auto">
              {botonesVista}
              {/* El mismo menú que el botón «Nuevo» de la barra lateral: MenuContextual lo pinta
                  en <body>, así que ya no lo esconde el `hidden sm:flex` del <aside>. */}
              <Button
                size="sm"
                className="sm:hidden rounded-full h-9 w-9 p-0"
                style={{ background: 'var(--gd-blue)', color: '#fff' }}
                onClick={abrirMenuNuevo}
                data-testid="drive-new-movil"
                aria-haspopup="menu"
                aria-expanded={!!newMenu}
                aria-label="Nuevo"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        <div className={`flex items-center min-w-0 ${isMobile ? '' : 'pt-2'}`} style={{ background: 'var(--gd-bg)' }}>
          <div className="gdrive-crumbs flex-1 min-w-0">
            {enCompartida ? (
              <>
                <button type="button" onClick={() => setCompartida(null)} data-testid="drive-crumb-compartidos">
                  Compartidos conmigo
                </button>
                {(contenido?.migas || [{ ruta: '/', label: compartida.name }]).map((m, i, todas) => (
                  <React.Fragment key={m.ruta}>
                    <ChevronRight className="h-3.5 w-3.5 opacity-50" />
                    <button
                      type="button"
                      className={i === todas.length - 1 ? 'current' : ''}
                      onClick={() => setCompartida((v) => ({ ...v, ruta: m.ruta }))}
                    >
                      {m.label}
                    </button>
                  </React.Fragment>
                ))}
              </>
            ) : view === 'my' ? crumbs.map((c, i) => (
              <React.Fragment key={c.path}>
                {i > 0 && <ChevronRight className="h-3.5 w-3.5 opacity-50" />}
                {/* Las migas también reciben sueltas: es la única forma de sacar
                    un archivo de la carpeta abierta hacia un nivel de arriba. */}
                <button
                  type="button"
                  className={`${i === crumbs.length - 1 ? 'current' : ''} ${destino === c.path ? 'is-destino' : ''}`}
                  onClick={() => setFolder(c.path)}
                  onContextMenu={(e) => abrirMenu(e, { tipo: 'carpeta', ruta: c.path })}
                  data-testid={`drive-crumb-${c.path}`}
                  {...propsCarpetaDestino(c.path)}
                >
                  {c.label}
                </button>
              </React.Fragment>
            )) : (
              <span className="current" style={{ fontWeight: 600, color: 'var(--gd-text)' }}>{NAV.find((n) => n.id === view)?.label}</span>
            )}
          </div>
          <div className="flex items-center gap-1 pr-4 shrink-0">
            {/* Dentro de una carpeta compartida como editor se puede añadir archivos */}
            {puedeSubirAqui && (
              <Button
                size="sm"
                variant="outline"
                className="rounded-full h-8"
                onClick={() => fileRef.current?.click()}
                data-testid="drive-subir-compartida"
              >
                <Upload className="h-4 w-4 mr-1" /> Subir aquí
              </Button>
            )}
            {!isMobile && botonesVista}
          </div>
        </div>

        {barraFiltros}

        {/* El clic derecho va en el ScrollArea y no en un envoltorio interno:
            así el hueco de debajo de la lista también abre el menú de crear. */}
        <ScrollArea
          className="flex-1 px-2 md:px-4 pb-4"
          onContextMenu={(e) => { if (view === 'my') abrirMenu(e, { tipo: 'fondo' }); }}
          data-testid="drive-fondo"
        >
          <div className="drive-contenido">
            {isFetching && !items.length && (
              <div className="py-20 grid place-items-center"><Loader2 className="h-8 w-8 animate-spin text-brand" /></div>
            )}
            {!isFetching && !visibles.length && !carpetasVisibles.length && (
              <div className="gdrive-empty">
                <div className="gdrive-empty-icon"><HardDrive className="h-9 w-9" strokeWidth={1.75} /></div>
                <p className="title">{view === 'shared' && !enCompartida ? 'Nadie te ha compartido nada todavía' : 'Esta carpeta está vacía'}</p>
                <p className="sub">
                  {view === 'shared'
                    ? 'Aquí aparecen los archivos y las carpetas que otras personas comparten contigo.'
                    : `Crea un documento, una hoja o una presentación: se abren en ${OFFICE_NAME}.`}
                </p>
              </div>
            )}

            {layout === 'list' && (visibles.length > 0 || carpetasVisibles.length > 0) && (
              <table className="gdrive-table w-full">
                <thead>
                  <tr>
                    <Columna campo="name">Nombre</Columna>
                    <th className="text-left hidden md:table-cell">Propietario</th>
                    <Columna campo="updated_at" className="hidden sm:table-cell">Última modificación</Columna>
                    <Columna campo="size" className="hidden lg:table-cell">Tamaño</Columna>
                    <th className="w-28" />
                  </tr>
                </thead>
                <tbody>
                  {/* Las carpetas van en la MISMA lista que los archivos, como
                      en cualquier explorador: antes estaban como etiquetas
                      sueltas arriba y no se parecían a lo que son. */}
                  {carpetasVisibles.map((c) => (
                    <tr
                      key={c.clave}
                      className={`gdrive-row is-carpeta ${destino === c.ruta ? 'is-destino' : ''}`}
                      onClick={c.abrir}
                      onContextMenu={c.ruta ? (e) => abrirMenu(e, { tipo: 'carpeta', ruta: c.ruta }) : undefined}
                      data-testid="drive-folder-row"
                      {...(c.ruta ? propsCarpetaDestino(c.ruta) : {})}
                    >
                      <td>
                        <div className="flex items-center gap-3 min-w-0">
                          <span className="gdrive-file-icon" style={{ background: 'var(--gd-hover)' }}>
                            <Folder className="h-4 w-4" style={{ color: 'var(--gd-text-sec)' }} />
                          </span>
                          <span className="name truncate">{c.name}</span>
                          {c.compartida && (
                            <Users className="h-3.5 w-3.5 shrink-0" style={{ color: 'var(--gd-text-sec)' }} aria-label="Compartida" />
                          )}
                        </div>
                      </td>
                      <td className="hidden md:table-cell sec">{c.dueno}</td>
                      <td className="hidden sm:table-cell sec">{fmtDate(c.updated_at) || '—'}</td>
                      <td className="hidden lg:table-cell sec">{fmtSize(c.size)}</td>
                      <td>
                        <div className="flex items-center justify-end gap-0.5 gdrive-row-actions" onClick={(e) => e.stopPropagation()}>
                          {c.ruta && (
                            <button
                              type="button"
                              className="gdrive-icon-btn"
                              onClick={(e) => abrirMenu(e, { tipo: 'carpeta', ruta: c.ruta })}
                              aria-label="Más acciones"
                              aria-haspopup="menu"
                              data-testid="drive-folder-more"
                            >
                              <MoreVertical className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {visibles.map((f) => {
                    const meta = KIND[f.kind] || KIND.file;
                    const Icon = meta.Icon;
                    return (
                      <tr
                        key={f.id}
                        className={`gdrive-row ${arrastrando === f.id ? 'is-arrastrando' : ''}`}
                        onClick={(e) => openFile(f, e)}
                        onContextMenu={(e) => abrirMenu(e, { tipo: 'archivo', file: f })}
                        draggable={puedeMover}
                        onDragStart={(e) => iniciarArrastre(e, f)}
                        onDragEnd={limpiarArrastre}
                        data-testid="drive-file-row"
                      >
                        <td>
                          <div className="flex items-center gap-3 min-w-0">
                            <span className="gdrive-file-icon" style={{ background: `${meta.color}1f` }}>
                              <Icon className="h-4 w-4" style={{ color: meta.color }} />
                            </span>
                            <span className="name truncate">{f.name}</span>
                            {f.starred && <Star className="h-3.5 w-3.5 gdrive-star-on shrink-0" />}
                          </div>
                        </td>
                        <td className="hidden md:table-cell sec">yo</td>
                        <td className="hidden sm:table-cell sec">{fmtDate(f.updated_at)}</td>
                        <td className="hidden lg:table-cell sec">{fmtSize(f.size)}</td>
                        <td>
                          <div className="flex items-center justify-end gap-0.5 gdrive-row-actions" onClick={(e) => e.stopPropagation()}>
                            {esPropietario(f) && (
                              <button type="button" className="gdrive-icon-btn" onClick={(e) => toggleStar(f, e)} aria-label="Destacar">
                                <Star className={`h-4 w-4 ${f.starred ? 'gdrive-star-on' : ''}`} />
                              </button>
                            )}
                            {view === 'trash' ? (
                              <button type="button" className="gdrive-icon-btn" onClick={(e) => restoreFile(f, e)} aria-label="Restaurar"><RotateCcw className="h-4 w-4" /></button>
                            ) : null}
                            <button type="button" className="gdrive-icon-btn" onClick={() => download(f)} aria-label="Descargar"><Download className="h-4 w-4" /></button>
                            {esPropietario(f) && (
                              <button type="button" className="gdrive-icon-btn is-danger" onClick={(e) => trashFile(f, e)} aria-label="Eliminar"><Trash2 className="h-4 w-4" /></button>
                            )}
                            {/* Sin clic derecho (móvil) este botón es la única
                                puerta a cambiar nombre y compartir. */}
                            <button
                              type="button"
                              className="gdrive-icon-btn"
                              onClick={(e) => abrirMenu(e, { tipo: 'archivo', file: f })}
                              aria-label="Más acciones"
                              aria-haspopup="menu"
                              data-testid="drive-row-more"
                            >
                              <MoreVertical className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}

            {layout === 'grid' && (visibles.length > 0 || carpetasVisibles.length > 0) && (
              <div className="gdrive-grid">
                {carpetasVisibles.map((c) => (
                  <button
                    key={c.clave}
                    type="button"
                    className={`gdrive-card is-carpeta ${destino === c.ruta ? 'is-destino' : ''}`}
                    onClick={c.abrir}
                    onContextMenu={c.ruta ? (e) => abrirMenu(e, { tipo: 'carpeta', ruta: c.ruta }) : undefined}
                    data-testid="drive-folder-card"
                    {...(c.ruta ? propsCarpetaDestino(c.ruta) : {})}
                  >
                    <div className="gdrive-card-thumb" style={{ background: 'var(--gd-hover)' }}>
                      <Folder className="h-10 w-10" style={{ color: 'var(--gd-text-sec)' }} />
                    </div>
                    <div className="gdrive-card-meta">
                      <Folder className="h-4 w-4 shrink-0" style={{ color: 'var(--gd-text-sec)' }} />
                      <span className="truncate text-[13px] font-medium">{c.name}</span>
                      {c.compartida && <Users className="h-3.5 w-3.5 shrink-0" style={{ color: 'var(--gd-text-sec)' }} />}
                      <span className="ml-auto shrink-0 text-[11px]" style={{ color: 'var(--gd-text-sec)' }}>{fmtSize(c.size)}</span>
                    </div>
                  </button>
                ))}
                {visibles.map((f) => {
                  const meta = KIND[f.kind] || KIND.file;
                  const Icon = meta.Icon;
                  return (
                    <button
                      key={f.id}
                      type="button"
                      className={`gdrive-card ${arrastrando === f.id ? 'is-arrastrando' : ''}`}
                      onClick={(e) => openFile(f, e)}
                      onContextMenu={(e) => abrirMenu(e, { tipo: 'archivo', file: f })}
                      draggable={puedeMover}
                      onDragStart={(e) => iniciarArrastre(e, f)}
                      onDragEnd={limpiarArrastre}
                      data-testid="drive-file-card"
                    >
                      <div className="gdrive-card-thumb" style={{ background: `${meta.color}14` }}>
                        <Icon className="h-10 w-10" style={{ color: meta.color }} />
                      </div>
                      <div className="gdrive-card-meta">
                        <Icon className="h-4 w-4 shrink-0" style={{ color: meta.color }} />
                        <span className="truncate text-[13px] font-medium">{f.name}</span>
                        {f.starred && <Star className="h-3 w-3 gdrive-star-on shrink-0" />}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </ScrollArea>

        {soltarExterno && (
          <div className="gdrive-drop-capa" data-testid="drive-drop-capa">
            <div className="caja">
              <Upload className="h-5 w-5" />
              <span className="truncate">Suelta para subir a {nombreCarpeta(destino || folder)}</span>
            </div>
          </div>
        )}
      </div>

      {menu && (
        <MenuContextual
          x={menu.x}
          y={menu.y}
          items={opcionesMenu()}
          onClose={cerrarMenu}
        />
      )}

      {newMenu && (
        <MenuContextual
          x={newMenu.x}
          y={newMenu.y}
          items={accionesNuevo()}
          onClose={() => setNewMenu(null)}
          testId="drive-new-menu"
          ancho
        />
      )}

      <DriveSharePanel
        open={!!compartir}
        onOpenChange={(v) => {
          if (v) return;
          setCompartir(null);
          qc.invalidateQueries({ queryKey: ['drive-folders'] }); // la marca de «compartida» pudo cambiar
        }}
        fileId={compartir?.tipo === 'archivo' ? compartir.id : undefined}
        fileName={compartir?.tipo === 'archivo' ? compartir.name : undefined}
        carpeta={compartir?.tipo === 'carpeta' ? { id: compartir.id, name: compartir.name } : undefined}
      />

      <DialogoTexto
        open={dialogo?.tipo === 'carpeta'}
        titulo="Nueva carpeta"
        descripcion={dialogo?.padre && dialogo.padre !== '/' ? `Se creará dentro de ${dialogo.padre.split('/').pop()}.` : 'Se creará en Mi unidad.'}
        etiqueta="Nombre de la carpeta"
        boton="Crear"
        onOpenChange={(v) => !v && setDialogo(null)}
        onConfirmar={(nombre) => crearCarpetaCon(nombre, dialogo?.padre ?? folder)}
      />
      <DialogoTexto
        open={dialogo?.tipo === 'renombrar'}
        titulo="Cambiar nombre"
        etiqueta="Nombre del archivo"
        valorInicial={dialogo?.archivo?.name || ''}
        boton="Guardar"
        onOpenChange={(v) => !v && setDialogo(null)}
        onConfirmar={(nombre) => renombrarCon(dialogo.archivo, nombre)}
      />
      <DialogoTexto
        open={dialogo?.tipo === 'renombrar-carpeta'}
        titulo="Cambiar nombre de la carpeta"
        descripcion="Los archivos que tiene dentro se quedan donde están."
        etiqueta="Nombre de la carpeta"
        valorInicial={(dialogo?.ruta || '').split('/').pop()}
        boton="Guardar"
        onOpenChange={(v) => !v && setDialogo(null)}
        onConfirmar={(nombre) => renombrarCarpeta(dialogo.ruta, nombre)}
      />
      <DialogoConfirmar
        open={!!aEliminar}
        titulo="¿Eliminar para siempre?"
        mensaje={`«${aEliminar?.name || ''}» se borra de Drive y no se puede recuperar, ni aquí ni desde la app.`}
        boton="Eliminar para siempre"
        onOpenChange={(v) => !v && setAEliminar(null)}
        onConfirmar={() => eliminarParaSiempre(aEliminar)}
      />
      <GaleriaPlantillas
        open={plantillasOpen}
        onOpenChange={setPlantillasOpen}
        folder={folder}
        onCreated={async (archivo) => {
          setPlantillasOpen(false);
          await qc.invalidateQueries({ queryKey: ['drive-files'] });
          irA(nav, editorUrlFor(archivo));
        }}
      />
      <DialogoCrearBuzon
        open={buzonOpen}
        onOpenChange={setBuzonOpen}
        folder={folder}
        onCreado={async (archivo) => {
          await qc.invalidateQueries({ queryKey: ['drive-files'] });
          irA(nav, editorUrlFor(archivo));
        }}
      />
      <CreateWithAIDialog
        open={aiCreateOpen}
        onOpenChange={setAiCreateOpen}
        folder={folder}
        onCreated={async (file) => {
          setAiCreateOpen(false);
          await qc.invalidateQueries({ queryKey: ['drive-files'] });
          irA(nav, editorUrlFor(file));
        }}
      />
    </div>
  );
}
